import PropTypes from "prop-types";
import PopupComponent, {
	POPUP_ACTION_BUTTON_LOCATION,
	PopupActionDataObject,
	PopupTabDataObject
} from "Core/components/PopupComponent";
import {connect} from "react-redux";
import * as pageConfig from "../../config";
import {getPageActions} from "Core/helpers/redux";
import * as actions from "../../actions";
import {cloneDeep, get, orderBy} from "lodash";
import {
	icon_font_close_symbol,
	icon_font_create_symbol, 
	icon_font_info_symbol,
	icon_font_save_symbol,
} from "Config/app";
import Button, {BUTTON_STYLE} from "Core/components/display/Button";
import {getBool, getString, isset} from "Core/helpers/data";
import {isSystemAdmin} from "../../../../../../helpers/currentUser";
import React from "react";
import Label from "Core/components/display/Label";

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in component.
 */
const mapStateToProps = state => ({

});

class ItemPopup extends PopupComponent {
	/**
	 * IMPORTANT! Must be defined in components that extend this abstract component like this:
	 * dirname = __dirname;
	 *
	 * @note This is done in order for automatic tab component loading to work properly.
	 */
	dirname = __dirname;

	constructor(props) {
		super(props, {
			translationPath: `${pageConfig.translationPath}.ItemPopup`,
			domPrefix: 'item-popup',
			hideSingleTab: true,
		});

		this.initialState = {
			/**
			 * List of all popup tabs
			 * @type {PopupTabDataObject[]}
			 */
			tabs: [],

			/**
			 * List of all popup actions
			 * @type {PopupActionDataObject[]}
			 */
			actions: [],

			/**
			 * ID of the currently opened tab
			 * @type {string}
			 */
			currentTabId: '',
		};

		this.state = cloneDeep(this.initialState);

		// Action methods
		this.saveMainTabData = this.saveMainTabData.bind(this);
		this.saveRolesTabData = this.saveRolesTabData.bind(this);
		this.saveSubAccountsTabData = this.saveSubAccountsTabData.bind(this);
	}


	// Component property methods ---------------------------------------------------------------------------------------
	/**
	 * Get component's ID that can be used as DOM element id attribute value
	 * @return {string}
	 */
	getDomId() { return this.getOption('domPrefix'); }


	// Tab methods ------------------------------------------------------------------------------------------------------
	/**
	 * Update dynamic action buttons that depend on current state and props
	 * @param {boolean} [isNew] - Flag that specifies if this popup is for a new item.
	 * @return {Promise<void>}
	 */
	async dynamicActionButtons({isNew}) {
		// Remove actions before deciding which ones to display
		await this.removeActions(['create', 'save-info', 'save-roles', 'save-sub-accounts']);

		// Add save related global actions
		if (!isNew) {
			// Save button on main tab
			await this.addAction(new PopupActionDataObject({
				id: 'save-info',
				location: POPUP_ACTION_BUTTON_LOCATION.TAB,
				tabs: ['MainTab'],
				action: this.saveMainTabData,
				buttonProps: {
					label: this.getTranslationPath('save_main_tab_action'),
					icon: icon_font_save_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 99
			}))
			
			// Save button on Account roles tab
			await this.addAction(new PopupActionDataObject({
				id: 'save-roles',
				location: POPUP_ACTION_BUTTON_LOCATION.TAB,
				tabs: ['RolesTab'],
				action: this.saveRolesTabData,
				buttonProps: {
					label: this.getTranslationPath('save_roles_tab_action'),
					icon: icon_font_save_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 99
			}))

			// Save button on Sub accounts tab
			await this.addAction(new PopupActionDataObject({
				id: 'save-sub-accounts',
				location: POPUP_ACTION_BUTTON_LOCATION.TAB,
				tabs: ['SubAccountsTab'],
				action: this.saveSubAccountsTabData,
				buttonProps: {
					label: this.getTranslationPath('save_sub_accounts_tab_action'),
					icon: icon_font_save_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 99
			}))
		} else {
			// Create button
			// @note Only available on the main tabs since only the main tab should be available on create.
			await this.addAction(new PopupActionDataObject({
				id: 'create',
				action: this.saveMainTabData,
				location: POPUP_ACTION_BUTTON_LOCATION.GLOBAL,
				buttonProps: {
					label: this.getTranslationPath('create_action'),
					icon: icon_font_create_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 0
			}));
		}
	}

	/**
	 * Update dynamic tabs that depend on current state and props
	 * 
	 * @param {boolean} [isNew] - Flag that specifies if this popup is for a new item.
	 * @return {Promise<unknown>}
	 */
	dynamicTabs({isNew}) {
		return Promise.allSettled(
			this.getTabs().map(tab => this.updateTab({
				...tab,
				visible: (tab.id === 'MainTab' || !isNew),
				componentProps: {...tab.componentProps, isNew},
			}))
		);
	}

	/**
	 * Initialize popup by specifying initial tabs, actions and current tab
	 * @note If current tab is not set it will default to the first visible and valid tab. Valid tab is tab that has
	 * 'component' property specified (manually or automatically loaded).
	 * @return {Promise<any>} Promise that resolves to entire component local state after state is updated.
	 */
	async init() {
		const isNew = getBool(this.props, 'isNew');

		// Add static actions that don't depend on current state or props
		let actions = [
			new PopupActionDataObject({
				id: 'close',
				action: this.close,
				buttonProps: {
					label: 'general.Close',
					icon: icon_font_close_symbol
				},
				ordinal: 0
			}),
		];
		await this.setActions(actions);
		// Add dynamic actions that depend on current state or props
		await this.dynamicActionButtons({isNew});

		// Add tabs
		await this.setTabs([
			new PopupTabDataObject({
				id: 'MainTab',
				icon: icon_font_info_symbol,
				label: this.tt('tab_name', 'MainTab'),
			}),
			new PopupTabDataObject({
				id: 'RolesTab',
				icon: 'not-fa icofont-key', 
				label: this.tt('tab_name', 'RolesTab'),
				loading: true,
				disabled: true,
			}),
			new PopupTabDataObject({
				id: 'SubAccountsTab',
				icon: 'not-fa icofont-hospital',
				label: this.tt('tab_name', 'SubAccountsTab'),
				loading: true,
				disabled: true,
			}),
		]).then(this.importTabComponents)
		// Update dynamic tabs that depend on current state or props
		await this.dynamicTabs({isNew});

		return Promise.resolve(this.state);
	}

	/**
	 * Try to automatically load tab components from standard location for tabs that don't have components defined
	 * @note To automatically load tab components the need to be located in a 'tabs' subdirectory either as a component
	 * file (like ./tabs/InfoTab.js) or subdirectory with index file (./tabs/InfoTab/index.js) where directory name or
	 * filename must be the tab ID.
	 *
	 * @return {Promise<any>} Promise that resolves to entire component local state after state is updated.
	 */
	importTabComponents() {
		const tabs = orderBy(this.getSortedTabs(), ['preloadPriority'], ['desc']);
		return Promise.all(tabs.map(tab => {
			if (!isset(tab.component)) return this.handleTabComponentImport(tab, import(`./tabs/${tab.id}`));
			else return Promise.resolve(this.state);
		}));
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Save data on the main tab.
	 * @note This method will handle both updating and creating a new item.
	 *
	 * @param {AccountsItemDataObject|Object[]} data - Main tab data on save or all tab data on create.
	 * @param {MouseEvent} event - Mouse click event for clicked action button DOM element.
	 * @param {'create'|'save-info'} actionId - ID of the clicked action.
	 * @return {Promise<*>}
	 */
	saveMainTabData(data, event, actionId) {
		const {
			redirectToItem, createAccountsItemAction, updateAccountsItemAction, loadAccountsListAction, 
			addSuccessMessageAction
		} = this.props;
		
		return this.getTabRef('MainTab').validateTab()
			.then(valid => {
				if(valid) {
					// Create item
					if(actionId === 'create') {
						/** @type {AccountsItemDataObject} */
						const item = get(data, 'MainTab');
						
						return this.executeAbortableAction(createAccountsItemAction, isSystemAdmin(), item)
							.then(createdItem => {
								if (createdItem?.id) {
									// Redirect to item URL if item was created successfully and show success message
									redirectToItem(createdItem.id);
									addSuccessMessageAction(this.t('create_success_msg'));

									// Reload item list (data table) without rendering the loading overlay
									// @note This is done asynchronously on purpose because there is no reason to wait for this 
									// to finish before continuing.
									this.executeAbortableAction(loadAccountsListAction, 
										undefined, undefined, undefined, undefined, undefined, false
									).then();
								}
								return createdItem;
							});
					}
					
					// Update item
					else if (actionId === 'save-info') {
						return this.executeAbortableAction(
							updateAccountsItemAction,
							isSystemAdmin(),
							getString(data, 'id', '', true),
							data
						)
							.then(response => {
								if (response) addSuccessMessageAction(this.t('save_main_tab_success_msg'));
								return response;
							});
					} else {
						console.error(`Invalid accounts item popup save method: ${actionId}`);
					}
				}
			});
	}

	/**
	 * Save data on the Roles tab
	 * 
	 * @param {AccountsItemRoleDataObject[]} rolesTabData - Roles tab data.
	 * @param {MouseEvent} event - Mouse click event for clicked action button DOM element.
	 * @returns {Promise<T>}
	 */
	saveRolesTabData(rolesTabData, event) {
		const {saveSelectedRolesAction, addSuccessMessageAction} = this.props;
		const accountId = getString(this.getTabData('MainTab'), 'id');

		return this.getTabRef('RolesTab').validateTab()
			.then(valid => {
				if(valid) {
					const roles = rolesTabData.filter(role => (role.roleId && role.userAccountId));
					return this.executeAbortableAction(saveSelectedRolesAction, isSystemAdmin(), accountId, roles)
						.then(resData => {
							if (isset(resData)) addSuccessMessageAction(this.t('save_roles_tab_success_msg'));
						});
				}});
	};

	/**
	 * Save data on the sub-accounts tab
	 * 
	 * @param {AccountsItemSubAccountsDataObject[]} subAccountsTabData - Sub-accounts tab data.
	 * @param {MouseEvent} event - Mouse click event for clicked action button DOM element.
	 * @returns {Promise<T>}
	 */
	saveSubAccountsTabData(subAccountsTabData, event) {
		const {saveSelectedSubAccountsAction, addSuccessMessageAction} = this.props;
		const accountId = getString(this.getTabData('MainTab'), 'id');
		
		return this.getTabRef('SubAccountsTab').validateTab()
			.then(valid => {
				if(valid) {
					return this.executeAbortableAction(
						saveSelectedSubAccountsAction, isSystemAdmin(), accountId, subAccountsTabData
					)
						.then(resData => {
							if (isset(resData)) addSuccessMessageAction(this.t('save_sub_accounts_tab_success_msg'));
						});
			}});
	};


	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render global action buttons
	 * @note Global actions are actions that apply to the whole popup.
	 *
	 * @return {JSX.Element|null}
	 */
	renderGlobalActionButtons() {
		const {isNew} = this.props;
		const actions = this.getVisibleActionsGlobal();

		return (
			actions.length > 0 ?
				<div className="popup-global-action-buttons">
					{
						!isNew ?
							<div className="popup-global-legend">
								<Label
									element="span"
									icon="info-circle"
									content={this.t('separate_save_notice')}
								/>
							</div>
							: null
					}
					
					{actions.map((action, index) =>
						<Button
							key={index}
							{...action.buttonProps}
							label={
								get(action.buttonProps, 'label') ?
									this.translatePath(get(action.buttonProps, 'label')) :
									''
							}
							disabled={action.disabled}
							onClick={e => this.handleGlobalActionButtonClick(e, action)}
						/>
					)}
				</div>
				: null
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
ItemPopup.propTypes = {
	// Flag that specifies if this popup is for creating a new item
	isNew: PropTypes.bool,
	// Function that will redirect to item URL
	redirectToItem: PropTypes.func, // Arguments: {string} - Item id to redirect to.

	// Events
	onClose: PropTypes.func,
	onGlobalAction: PropTypes.func,
	onTabAction: PropTypes.func,
};

export default connect(
	mapStateToProps, getPageActions(actions), null, {forwardRef: true}
)(ItemPopup);